const API_BASE = '/wp-json/symple-pos/v1';

let _storeUrl = '';
let _authHeader = '';
let _onUnauthorized = null;

/**
 * Decode common HTML entities in strings from the WooCommerce API.
 */
const ENTITY_MAP = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#039;': "'",
  '&#8211;': '\u2013',
  '&#8212;': '\u2014',
  '&#8216;': '\u2018',
  '&#8217;': '\u2019',
  '&#8220;': '\u201C',
  '&#8221;': '\u201D',
};
const ENTITY_RE = /&(?:amp|lt|gt|quot|#039|#8211|#8212|#8216|#8217|#8220|#8221);/g;

function decodeEntities(str) {
  return str.replace(ENTITY_RE, (match) => ENTITY_MAP[match] || match);
}

function decodeResponse(data) {
  if (typeof data === 'string') return decodeEntities(data);
  if (Array.isArray(data)) return data.map(decodeResponse);
  if (data && typeof data === 'object') {
    const out = {};
    for (const key in data) {
      out[key] = decodeResponse(data[key]);
    }
    return out;
  }
  return data;
}

/**
 * Configure the API client with store credentials.
 */
function configure(storeUrl, username, appPassword) {
  // Normalize: strip trailing slash
  _storeUrl = storeUrl.replace(/\/+$/, '');
  _authHeader = 'Basic ' + btoa(username + ':' + appPassword);
}

/**
 * Clear stored credentials.
 */
function disconnect() {
  _storeUrl = '';
  _authHeader = '';
}

/**
 * Whether the client has been configured with credentials.
 */
function isConfigured() {
  return Boolean(_storeUrl && _authHeader);
}

/**
 * Register a callback for 401 responses (auto-disconnect).
 */
function onUnauthorized(callback) {
  _onUnauthorized = callback;
}

/**
 * Internal fetch wrapper.
 */
async function apiFetch({ path, method = 'GET', data } = {}) {
  if (!_storeUrl || !_authHeader) {
    throw new Error('API client not configured. Call configure() first.');
  }

  const url = `${_storeUrl}${API_BASE}${path}`;
  const options = {
    method,
    headers: {
      'Authorization': _authHeader,
      'Content-Type': 'application/json',
    },
  };

  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(url, options);

  if (response.status === 401) {
    if (_onUnauthorized) {
      _onUnauthorized();
    }
    const err = new Error('Unauthorized');
    err.status = 401;
    throw err;
  }

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText };
    }
    const err = new Error(errorData.message || errorData.reason || errorData.error || `HTTP ${response.status}`);
    err.status = response.status;
    err.data = errorData;
    throw err;
  }

  const json = await response.json();
  return decodeResponse(json);
}

const api = {
  configure,
  disconnect,
  isConfigured,
  onUnauthorized,

  getProducts({ search = '', category = '', page = 1, per_page = 20 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    params.set('page', page);
    params.set('per_page', per_page);

    return apiFetch({ path: `/products?${params}` });
  },

  getProductBySku(sku) {
    return apiFetch({
      path: `/products/barcode/${encodeURIComponent(sku)}`,
    });
  },

  getConfig() {
    return apiFetch({ path: `/config` });
  },

  getCustomers({ search = '', page = 1 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    params.set('page', page);

    return apiFetch({ path: `/customers?${params}` });
  },

  createCustomer(data) {
    return apiFetch({
      path: `/customers`,
      method: 'POST',
      data,
    });
  },

  createOrder(data) {
    return apiFetch({
      path: `/orders`,
      method: 'POST',
      data,
    });
  },

  getSalesReport({ date_from, date_to } = {}) {
    const params = new URLSearchParams();
    if (date_from) params.set('date_from', date_from);
    if (date_to) params.set('date_to', date_to);

    return apiFetch({ path: `/reports/sales?${params}` });
  },

  getCategories() {
    return apiFetch({ path: `/categories` });
  },

  getSettings() {
    return apiFetch({ path: `/settings` });
  },

  saveSettings(data) {
    return apiFetch({
      path: `/settings`,
      method: 'POST',
      data,
    });
  },

  updateStock(productId, quantity) {
    return apiFetch({ path: `/products/${productId}/stock`, method: 'POST', data: { quantity } });
  },

  createProduct(data) {
    return apiFetch({
      path: `/products`,
      method: 'POST',
      data,
    });
  },

  validateCoupon(code) {
    return apiFetch({ path: '/coupons/validate', method: 'POST', data: { code } });
  },

  redeemCoupon(data) {
    return apiFetch({ path: '/coupons/redeem', method: 'POST', data });
  },

  loadGiftCard(data) {
    return apiFetch({ path: '/gift-cards/load', method: 'POST', data });
  },

  checkGiftCard(code) {
    return apiFetch({ path: '/gift-cards/check', method: 'POST', data: { code } });
  },

  redeemGiftCard(data) {
    return apiFetch({ path: '/gift-cards/redeem', method: 'POST', data });
  },

  searchCrmContacts(search) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    return apiFetch({ path: `/crm/contacts?${params}` });
  },

  syncContactToCrm(data) {
    return apiFetch({ path: '/crm/contacts/sync', method: 'POST', data });
  },
};

export default api;
